import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Calendar, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import apiService from '../../services/api';
import {
  AdminPageLayout,
  Section,
  Card,
  CardContent,
  Badge,
  Button
} from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import {
  AttributeStack,
  AttributeGrid
} from '../../components/admin/patterns';
import { useToast } from '../../contexts/ToastContext';

const SeasonManagement = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  const loadSeasons = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getSeasons();
      setSeasons(response.seasons || []);

      try {
        const currentSeasonResponse = await apiService.getCurrentSeason();
        setCurrentSeason(currentSeasonResponse);
      } catch (currentSeasonErr) {
        setCurrentSeason(null);
      }
    } catch (err) {
      console.error('Error loading seasons:', err);
      showError('Failed to load seasons');
      setSeasons([]);
      setCurrentSeason(null);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleActivateSeason = async (season) => {
    try {
      await apiService.activateSeason(season.id);
      showSuccess(`Season "${season.name}" activated successfully`);
      await loadSeasons();
    } catch (err) {
      console.error('Error activating season:', err);
      showError('Failed to activate season');
    }
  };

  const filteredSeasons = seasons.filter(season => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!season.name?.toLowerCase().includes(searchTerm) &&
        !season.description?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    if (filters.status === 'active' && season.status !== 'active') {
      return false;
    }
    if (filters.status === 'inactive' && season.status === 'active') {
      return false;
    }
    return true;
  });

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search season name or description..."
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Seasons' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        }
      ]}
      activeFilters={{ status: filters.status }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', status: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction
          onClick={() => navigate('/admin/seasons/add')}
          label="New Season"
        />
      }
    />
  );

  return (
    <AdminPageLayout
      title="SEASON MANAGEMENT"
      subtitle="Manage club seasons and time periods"
      backPath="/admin/club"
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
        {/* Current Season */}
        <Section title="CURRENT SEASON">
          <Card level={1}>
            <CardContent>
              {currentSeason ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '3.5rem', alignItems: 'center' }}>
                    <div style={{ background: 'var(--color-success-light)', padding: '1.25rem', borderRadius: '16px', color: 'var(--color-success)' }}>
                      <Trophy size={40} />
                    </div>
                    <AttributeStack
                      label="Active Season"
                      value={currentSeason.name}
                      variant="large"
                    />
                    <AttributeStack
                      label="Date Range"
                      value={`${currentSeason.start_date} — ${currentSeason.end_date}`}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '2.5rem' }}>
                    <AttributeStack
                      label="Calendars"
                      value={currentSeason.total_calendars || 0}
                      variant="large"
                    />
                    <AttributeStack
                      label="Programs"
                      value={currentSeason.total_programs || 0}
                      variant="large"
                    />
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', opacity: 0.6 }}>
                  <AlertCircle size={24} />
                  <span>No active season. Create a season to get started.</span>
                </div>
              )}
            </CardContent>
          </Card>
        </Section>

        {/* All Seasons */}
        <Section title="ALL SEASONS">
          <Listing
            loading={loading}
            items={filteredSeasons}
            actions={headerActions}
            filters={filterBar}
            variant="list"
            renderItem={(season) => (
              <ListingItem
                key={season.id}
                title={season.name}
                subtitle={season.description}
                icon={<Clock size={28} />}
                iconVariant={season.status === 'active' ? 'success' : 'secondary'}
                actions={
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <EditAction size="sm" onClick={() => navigate(`/admin/seasons/${season.id}/edit`)} />
                    {season.status !== 'active' && (
                      <Button
                        onClick={() => handleActivateSeason(season)}
                        variant="primary"
                        size="xs"
                      >
                        ACTIVATE
                      </Button>
                    )}
                  </div>
                }
              >
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border-light)' }}>
                  <AttributeGrid columns={3} gap="2rem">
                    <AttributeStack
                      label="Season Period"
                      value={`${season.start_date} — ${season.end_date}`}
                    />
                    {season.default_registration_start && (
                      <AttributeStack
                        label="Registration Window"
                        value={`${season.default_registration_start} — ${season.default_registration_end}`}
                      />
                    )}
                    <AttributeStack
                      label="Status"
                      value={
                        <Badge variant={season.status === 'active' ? 'success' : 'outline'}>
                          {season.status.toUpperCase()}
                        </Badge>
                      }
                    />
                  </AttributeGrid>

                  {(season.total_calendars !== undefined || season.total_programs !== undefined) && (
                    <div style={{ marginTop: '1.5rem', display: 'flex', gap: '3rem' }}>
                      <AttributeStack
                        label="Linked Calendars"
                        value={season.total_calendars || 0}
                        variant="small"
                      />
                      <AttributeStack
                        label="Linked Programs"
                        value={season.total_programs || 0}
                        variant="small"
                      />
                    </div>
                  )}
                </div>
              </ListingItem>
            )}
            emptyMessage="No seasons found"
          />
        </Section>
      </div>
    </AdminPageLayout>
  );
};

export default SeasonManagement;